import Vars from '@/config/vars';
import store from '@/store';
import { logException } from '@/helpers/cieloLogger';
import axios from '@/config/axios.config';

const afterLoadHandler = async () => {
    const params = {
        // required DPA Identifier, generated during registration.
        srcDpaId: store.state.clickToPayPayment.dpaId,
        dpaData: {
            dpaName: store.state.clickToPayPayment.dpaName? store.state.clickToPayPayment.dpaName: 'Checkout Cielo'
        },
        dpaTransactionOptions: {   
            dpaLocale: 'pt_BR',
            confirmPayment: false,
            dpaBillingPreference: 'FULL',
            paymentOptions:[
                {
                    dynamicDataType: 'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM'
                }
            ]
        },
        cardBrands: store.state.clickToPayPayment.supportedBrandCards, 
        checkoutExperience: 'WITHIN_CHECKOUT', // "PAYMENT_SETTINGS" || "WITHIN_CHECKOUT"
    };

    
    try {
        let recognitionTokenResponse;

        if (Vars.USE_SIMULATED_MODE)
            recognitionTokenResponse = await new Promise(resolve => setTimeout(() => resolve({data: {'token': 'abc'}}), 3000));
        else
            recognitionTokenResponse = await axios.get('/recognitionToken/getToken');
  
        if (!recognitionTokenResponse.data) {
            throw new Error('Network response was not ok ');
        }

        if(recognitionTokenResponse.data.token) {
            params.recognitionToken = recognitionTokenResponse.data.token;
        }
    } catch (error) {
        logException(error);
    }

    window.mcCheckoutService = new MastercardCheckoutServices();
    try {
        // No other library methods should be invoked until `init` resolves
        const promiseResolvedPayload = await window.mcCheckoutService.init(params);
        store.state.clickToPayPayment.initialized = true;
        store.state.clickToPayPayment.availableCardBrands = promiseResolvedPayload.availableCardBrands;

        const cards = await window.mcCheckoutService.getCards();
        store.state.clickToPayPayment.cards = cards;
    } catch (promiseRejectedPayload) {
        logException(promiseRejectedPayload);
    }
};

export const initClickToPayScript = async () => {
    if(store.state.clickToPayPayment.initialized) {
        return;
    }

    const dpaId = store.state.clickToPayPayment.dpaId;
    if(!dpaId) {
        return;
    }

    let c2pScr = Vars.ENVIRONMENT === 'PRD'?
        `https://src.mastercard.com/srci/integration/2/lib.js?srcDpaId=${dpaId}&locale=pt_BR`:
        `https://sandbox.src.mastercard.com/srci/integration/2/lib.js?srcDpaId=${dpaId}&locale=pt_BR`;
    let clickToPayScript = document.createElement('script');
    clickToPayScript.src = c2pScr;

    const scriptLoading = new Promise((resolve, reject) => {
        clickToPayScript.addEventListener('load', async () => {
            await afterLoadHandler();
            resolve();
        });
        document.head.appendChild(clickToPayScript);
    });

    await scriptLoading;
};

const createiFrame = () => {
    const isIframeCreated = document.querySelector('#iframe-click-to-pay-container');
    if(isIframeCreated) {
        return;
    }

    const iframeContainer = document.createElement('div');
    iframeContainer.setAttribute('id', 'iframe-click-to-pay-container');
    iframeContainer.classList.add('click-to-pay-iframe-container');

    const iframe = document.createElement('iframe');
    iframe.classList.add('click-to-pay-iframe');
    iframeContainer.appendChild(iframe);
    const appRoot = document.querySelector('#app');
    appRoot.appendChild(iframeContainer);
    appRoot.classList.add('with-iframe');

    return iframe;
};

const closeiFrame = () => {
    const iframe = document.querySelector('#iframe-click-to-pay-container');
    if (iframe) {
        iframe.remove();
        const appRoot = document.querySelector('#app');
        appRoot.classList.remove('with-iframe');
    }
};

const checkoutWithVisaCard = async (consumer, rememberMeAgreement, iframe, card) => {
    const visaConsumerPayload = {
        nationalIdentifier: consumer.nationalIdentifier,
        lastName: consumer.lastName || card.cardholderLastName,
        firstName: consumer.firstName || card.cardholderFirstName
    };

    // Test Address: 900 Metro Center Blvd, Foster City, CA 94404
    const sandboxBillingAddress = {
        name: 'Plinio Leme',
        line1: '900 Metro Center Blvd',
        city: 'Foster City',
        state: 'CA',
        zip: '94404',
        countryCode: 'US',
    };

    const billingAddress = Vars.ENVIRONMENT !== 'PRD'? sandboxBillingAddress: card.billingAddress;

    const encryptCardResult = await window.mcCheckoutService.encryptCard({
        ...card,
        billingAddress,
    });

    if(consumer.emailAddress) {
        visaConsumerPayload.emailAddress = consumer.emailAddress;
    }
    if(consumer.mobileNumber && consumer.mobileNumber.phone) {
        visaConsumerPayload.mobileNumber = {
            phoneNumber: consumer.phone,
            countryCode: consumer.countryCode
        };
    }

    const checkoutWithNewCardResult = await window.mcCheckoutService.checkoutWithNewCard({
        encryptedCard: encryptCardResult.encryptedCard,
        cardBrand: encryptCardResult.cardBrand,
        windowRef: iframe.contentWindow,
        recognitionTokenRequested: rememberMeAgreement,
        consumer: visaConsumerPayload,
        dpaTransactionOptions: {
            consumerEmailAddressRequested: true,
            consumerPhoneNumberRequested: true,
            consumerNameRequested: true,
            paymentOptions: [
                {
                    dynamicDataType: 'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM'
                }
            ]
        },
        complianceSettings: {
            privacy: {
                acceptedVersion: 'LATEST',
                latestVersion: 'LATEST',
                latestVersionUri: 'https://www.mastercard.com/global/click-to-pay/country-listing/privacy.html'
            },
            tnc: {
                acceptedVersion: 'LATEST',
                latestVersion: 'LATEST',
                latestVersionUri: 'https://www.mastercard.com/global/click-to-pay/country-listing/terms.html'
            }
        }
    });

    return checkoutWithNewCardResult;
};

export const encryptAndCheckoutWithNewCard = async (card, consumer, rememberMeAgreement) => {
    try {
        const encryptCardResult = await window.mcCheckoutService.encryptCard({
            ...card,
        });

        const iframe = createiFrame();

        if(encryptCardResult.cardBrand === 'visa') {
            const checkoutWithNewCardResult = await checkoutWithVisaCard(consumer, rememberMeAgreement, iframe, card);
            closeiFrame();
            return checkoutWithNewCardResult;
        }

        const checkoutWithNewCardResult = await window.mcCheckoutService.checkoutWithNewCard({
            encryptedCard: encryptCardResult.encryptedCard,
            cardBrand: encryptCardResult.cardBrand,
            windowRef: iframe.contentWindow,
            recognitionTokenRequested: rememberMeAgreement,
            consumer,
            dpaTransactionOptions: {
                consumerEmailAddressRequested: true,
                consumerPhoneNumberRequested: true,
                consumerNameRequested: true,
                paymentOptions:[
                    {
                        dynamicDataType: 'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM'
                    }
                ]
            },
            complianceSettings: {
                privacy: {
                    acceptedVersion: 'LATEST',
                    latestVersion: 'LATEST',
                    latestVersionUri: 'https://www.mastercard.com/global/click-to-pay/country-listing/privacy.html'
                },
                tnc: {
                    acceptedVersion: 'LATEST',
                    latestVersion: 'LATEST',
                    latestVersionUri: 'https://www.mastercard.com/global/click-to-pay/country-listing/terms.html'
                }
            }
        });
        closeiFrame();
        return checkoutWithNewCardResult;
    } catch(error) {
        closeiFrame();
        console.error('Erro C2P - encryptAndCheckoutWithNewCard', error); //Remover depois dos testes C2P
        logException(error);
        return null;
    }
};

export const checkoutWithCard = async (srcDigitalCardId, rememberMeAgreement) => {
    try {
        const iframe = createiFrame();
        const checkoutWithCardResult = await window.mcCheckoutService.checkoutWithCard({
            srcDigitalCardId: srcDigitalCardId,
            windowRef: iframe.contentWindow,
            recognitionTokenRequested: rememberMeAgreement,
            dpaTransactionOptions: {
                consumerEmailAddressRequested: true,
                consumerPhoneNumberRequested: true,
                consumerNameRequested: true,
                paymentOptions:[
                    {
                        dynamicDataType: 'CARD_APPLICATION_CRYPTOGRAM_SHORT_FORM'
                    }
                ]
            },
            complianceSettings: {
                privacy: {
                    acceptedVersion: 'LATEST',
                    latestVersion: 'LATEST',
                    latestVersionUri: 'https://www.mastercard.com/global/click-to-pay/country-listing/privacy.html'
                },
                tnc: {
                    acceptedVersion: 'LATEST',
                    latestVersion: 'LATEST',
                    latestVersionUri: 'https://www.mastercard.com/global/click-to-pay/country-listing/terms.html'
                }
            }
        });
        closeiFrame();
        return checkoutWithCardResult;
    } catch(error) {
        closeiFrame();
        console.error('Erro C2P - checkoutWithCard', error); //Remover depois dos testes C2P
        logException(error);
        return null;
    }
};

export const splitNames = (fullName) => {
    if(!fullName) {
        return {
            firstName: '',
            lastName: '',
        };
    }

    const names = fullName.split(' ');

    if(names.length === 0) {
        return {
            firstName: '',
            lastName: '',
        };
    }

    if(names.length === 1) {
        return {
            firstName: names[0],
            lastName: '',
        };
    }

    return {
        firstName: names[0],
        lastName: names[names.length - 1],
    };
};